import { useRef, useState } from 'react';
import { IQuotePurchaseOrder, IQuotePurchaseTrackingOrder, QUOTE_STATUS_CODE, TABS } from 'types/quoteDetails.types';
import { IQuoteItem } from 'types/quote.types';
import { QuoteService } from 'services/quote.service';
import QuoteDeviceApprovalCard from '../QuoteDeviceApprovalCard/QuoteDeviceApprovalCard';
import QuoteDeviceApprovalCardSkeleton from '../QuoteDeviceApprovalCard/components/QuoteDeviceApprovalCardSkeleton/QuoteDeviceApprovalCardSkeleton';
import QuotePurchaseTrackingCard from '../QuotePurchaseTrackingCard/QuotePurchaseTrackingCard';
import QuotePurchaseTrackingCardSkeleton from '../QuotePurchaseTrackingCard/components/QuotePurchaseTrackingCardSkeleton/QuotePurchaseTrackingCardSkeleton';
import './QuoteDetailDevices.scss';

interface IQuoteDetailDevices {
  items?: IQuoteItem[];
  purchaseTracking?: IQuotePurchaseOrder[];
  isLoading?: boolean;
  defaultActiveTab?: TABS;
  onTabChange?: (id: TABS) => void;
  quoteStatusCode?: QUOTE_STATUS_CODE;
  quoteId?: number;
}

/**
 * Lista de dispositivos de la cotización con título, menú de pestañas y tarjetas.
 */
const QuoteDetailDevices = ({
  items,
  purchaseTracking,
  isLoading,
  defaultActiveTab = TABS.APPROVAL,
  onTabChange,
  quoteStatusCode,
  quoteId
}: IQuoteDetailDevices) => {
  const [activeTab, setActiveTab] = useState<TABS>(defaultActiveTab);
  const [purchaseTrackingData, setPurchaseTrackingData] = useState<IQuotePurchaseTrackingOrder[]>();
  const [isLoadingTracking, setIsLoadingTracking] = useState(false);
  const hasFetchedTracking = useRef(false);
  const hideTabsStatuses = [QUOTE_STATUS_CODE.PENDING, QUOTE_STATUS_CODE.TO_QUOTE, QUOTE_STATUS_CODE.CANCELLED];
  const hideTabs = hideTabsStatuses.includes(quoteStatusCode as QUOTE_STATUS_CODE);
  const activeApprovalView = activeTab === TABS.APPROVAL && !isLoading;
  const activePurchaseTrackingView = activeTab === TABS.PURCHASE_TRACKING && !isLoading;

  /** Fetches purchase tracking data once when switching to PURCHASE_TRACKING tab. */
  const fetchPurchaseTracking = async () => {
    if (hasFetchedTracking.current || !quoteId) return;
    hasFetchedTracking.current = true;
    setIsLoadingTracking(true);
    try {
      const response = await QuoteService.getPurchaseTracking(quoteId);
      setPurchaseTrackingData(response);
    } catch {
      hasFetchedTracking.current = false;
    } finally {
      setIsLoadingTracking(false);
    }
  };

  const handleTabChange = (id: TABS) => {
    if (isLoading) return;
    setActiveTab(id);
    onTabChange?.(id);
    if (id === TABS.PURCHASE_TRACKING) fetchPurchaseTracking();
  };

  const tabs = [
    { id: TABS.APPROVAL, label: 'Aprobación del cliente' },
    { id: TABS.PURCHASE_TRACKING, label: 'Seguimiento de compra' }
  ];

  return (
    <div className="quoteDetailDevices">
      <div className="quoteDetailDevicesContainer">
        <div className="devicesTitle">{hideTabs ? 'Herramientas a cotizar' : 'Herramientas cotizadas'}</div>
        {!hideTabs && (
          <div className="devicesMenu">
            <div className="tabBar">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`tabBtn${activeTab === tab.id ? ' active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="devicesCards">
          {isLoading && (
            <div className="devicesLoader">
              {Array.from({ length: 20 }, (_, i) => (
                <QuoteDeviceApprovalCardSkeleton key={i} />
              ))}
            </div>
          )}
          {activeApprovalView && (
            <div className="approvalView">
              {items?.map(device => (
                <QuoteDeviceApprovalCard key={device.quoteItemId} device={device} quoteId={quoteId} />
              ))}
            </div>
          )}
          {activePurchaseTrackingView && (
            <div className="purchaseTrackingView">
              {isLoadingTracking
                ? Array.from({ length: 3 }, (_, i) => <QuotePurchaseTrackingCardSkeleton key={i} />)
                : purchaseTrackingData?.map((trackingData, index) => (
                    <QuotePurchaseTrackingCard
                      key={`purchase-tracking-${index}-${trackingData.orderGenerated}`}
                      trackingData={trackingData}
                    />
                  ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteDetailDevices;
